import React, { useState, useEffect, useRef } from 'react'
import { useAsync } from 'react-use'

import { Card, Grid, Dropdown, Image, Button, Space, List, FloatingPanel, ProgressCircle, Dialog, Divider, Skeleton } from 'antd-mobile'
import { DownCircleOutlined } from '@ant-design/icons'

import { startPronunciationAssessment, stopPronunciationAssessment, textToSpeech } from '../../utils/speech'
import { sleep } from '../../utils/tools'

import { conversationList } from '../../const/conversation'

import NoProfile from '../../assets/images/svg/noprofile.svg'
import profile from '../../assets/images/svg/profile.svg'
import speakerIcon from '../../assets/images/svg/speaker.svg'
import turtleIcon from '../../assets/images/svg/turtle.svg'
import translateIcon from '../../assets/images/svg/translation.svg'
import microphoneIcon from '../../assets/images/svg/microphone.svg'
import stopIcon from '../../assets/images/svg/stop.svg'
import nextIcon from '../../assets/images/svg/next.svg'

const level = (score) => {
  return ((!score) || (score < 50)) ? '--adm-color-danger' : (score < 80) ? '--adm-color-warning' : '--adm-color-success'
}

let reco

const Form = () => {
  const ref = useRef(null)
  const [loading, setLoading] = useState(false)
  const [isRec, setIsRec] = useState(false)
  const [selectedItem, setSelectedItem] = useState()
  const [ind, setInd] = useState(0)
  const [result, setResult] = useState()
  const [score, setScore] = useState({})
  const [convH, setConvH] = useState(0)

  const { value } = useAsync(async () => {
    setSelectedItem(conversationList[0])
    const h = (conversationList[0]?.conversation) ? conversationList[0].conversation.length * 200 + 250 : 1920
    setConvH(h)
    return conversationList
  }, [])

  useEffect(() => {
    const goToBottom = document.querySelector('#conversation')
    if (goToBottom) {
      goToBottom.scrollTo({ top: convH, left: 0, behavior: 'smooth' })
    }
  }, [ind, result, convH])

  const handleRecord = async (rec, isB) => {
    try {
      setLoading(true)
      if (isB) {
        if (!rec) {
          const re = await stopPronunciationAssessment(reco)
          if (re.detailedResult?.RecognitionStatus === 'Success') {
            setScore({ accuracy: re.accuracyScore, fluency: re.fluencyScore, completeness: re.completenessScore })
            setResult(re.detailedResult.DisplayText)
          } else {
            setScore({})
            setResult()
            Dialog.alert({
              content: `${re.detailedResult?.RecognitionStatus ?? 'Fail'}! Please do it again!`,
              closeOnMaskClick: true,
              confirmText: 'OK'
            })
          }
          await sleep(5000)
          let n = ind
          do {
            n++
            if (n < selectedItem.conversation.length) {
              setScore({})
              setResult()
              setInd(n)
              if (selectedItem.conversation[n].role === 'a') {
                await textToSpeech(selectedItem.conversation[n].sentence, selectedItem.lang)
                await sleep(5000)
              }
            }
          } while ((n < selectedItem.conversation.length) && (selectedItem.conversation[n].role === 'a'))
          setIsRec(rec)
        } else {
          reco = await startPronunciationAssessment(selectedItem.conversation[ind].sentence, selectedItem.lang)
          setIsRec(rec)
        }
      }
      setLoading(false)
      return Promise.resolve()
    } catch (error) {
      setLoading(false)
      return Promise.reject(error)
    }
  }

  const handleNext = async () => {
    await textToSpeech(selectedItem.conversation[ind].sentence, selectedItem.lang)
    await sleep(3000)
    if ((ind + 1) < selectedItem.conversation.length) {
      setInd(ind + 1)
    }
  }

  if (!value) return <Skeleton></Skeleton>
  return (
    <div style={{ position: 'relative' }}>
      <Card style={{ border: '1px solid #e1e1e1' }}>
        <Grid columns={24} gap={8}>
          <Grid.Item span={4}>
            <div style={{ marginTop: 10, fontWeight: 'bold', textDecoration: 'underline' }}>Topic</div>
          </Grid.Item>
          <Grid.Item span={20}>
            <Dropdown ref={ref}>
              <Dropdown.Item key='items' arrowIcon={<DownCircleOutlined style={{ fontSize: 16 }} />} title={selectedItem.title}>
                <List mode='card' style={{ margin: '0 16px' }}>
                  {(value.map((o, i) => (
                    <List.Item
                      key={i}
                      arrowIcon={false}
                      onClick={() => {
                        if (selectedItem.title !== o.title) {
                          setInd(0)
                          setSelectedItem(o)
                          setScore({})
                          setResult()
                          const h = (o?.conversation) ? o.conversation.length * 200 + 250 : 1920
                          setConvH(h)
                        }
                        ref.current?.close()
                      }}
                    >
                      {`${i + 1}. ${o.title}`}
                    </List.Item>
                  )))}
                </List>
              </Dropdown.Item>
            </Dropdown>
          </Grid.Item>
        </Grid>
      </Card>
      <div id='conversation'>
        <Space direction='vertical'>
          {Array.from({ length: (ind + 1) }, (_, i) => (
            <div key={i} style={{ margin: '8px 0' }}>
              {(selectedItem.conversation[i].role === 'a') ? (
                <Grid columns={8} gap={8}>
                  <Grid.Item span={1}>
                    <Image
                      src={profile}
                      width={32}
                      fit='cover'
                      style={{ borderRadius: 32 }}
                    />
                  </Grid.Item>
                  <Grid.Item span={7}>
                    <Card style={{ border: '1px solid #e1e1e1' }}>
                      <span style={{ fontWeight: 'bold', color: '#50514e' }}>
                        {selectedItem.conversation[i].sentence}
                      </span><br />
                      <span style={{ fontSize: 12, fontStyle: 'italic', color: '#9b9898' }}>
                        {selectedItem.conversation[i].pronounce}
                      </span><br />
                      <span style={{ color: '#8b8787' }}>
                        {selectedItem.conversation[i].translate}
                      </span>
                      <div style={{ margin: '8px 0 0 -8px' }}>
                        <Button fill='none' onClick={async () => await textToSpeech(selectedItem.conversation[i].sentence, selectedItem.lang)}><Image src={speakerIcon} height={16} fit='contain' /></Button>
                        <Button fill='none'><Image src={turtleIcon} height={16} fit='contain' /></Button>
                        <Button fill='none'><Image src={translateIcon} height={16} fit='contain' /></Button>
                      </div>
                    </Card>
                  </Grid.Item>
                </Grid>
              ) : (
                <Grid columns={8} gap={8}>
                  <Grid.Item span={7}>
                    <Card style={{ border: '1px solid #e1e1e1' }}>
                      <span style={{ fontWeight: 'bold', color: '#50514e' }}>
                        {selectedItem.conversation[i].sentence}
                      </span><br />
                      <span style={{ fontSize: 12, fontStyle: 'italic', color: '#9b9898' }}>
                        {selectedItem.conversation[i].pronounce}
                      </span><br />
                      <span style={{ color: '#8b8787' }}>
                        {selectedItem.conversation[i].translate}
                      </span>
                      <div style={{ margin: '8px 0 0 -8px' }}>
                        <Button fill='none' onClick={async () => await textToSpeech(selectedItem.conversation[i].sentence, selectedItem.lang)}><Image src={speakerIcon} height={16} fit='contain' /></Button>
                        <Button fill='none'><Image src={turtleIcon} height={16} fit='contain' /></Button>
                        <Button fill='none'><Image src={translateIcon} height={16} fit='contain' /></Button>
                      </div>
                    </Card>
                  </Grid.Item>
                  <Grid.Item span={1}>
                    <Image
                      src={NoProfile}
                      width={32}
                      fit='cover'
                      style={{ borderRadius: 32 }}
                    />
                  </Grid.Item>
                </Grid>
              )}
            </div>
          ))}
          {(selectedItem.conversation[ind].role === 'b' && result) && (
            <Card style={{ margin: '8px 0', backgroundColor: '#7feda0', border: '1px solid #e1e1e1', borderRadius: 12, boxShadow: '2px 2px 2px 1px #e1e1e1' }}>
              <div style={{ fontWeight: 'bold', color: '#50514e' }}>
                {result}
              </div>
              <Divider />
              <Grid columns={24} gap={8}>
                <Grid.Item span={6} style={{ marginTop: 16 }}>
                  Your score:
                </Grid.Item>
                <Grid.Item span={6} style={{ textAlign: 'center' }}>
                  <ProgressCircle
                    percent={score?.accuracy ?? 0}
                    style={{ '--size': '48px', '--fill-color': `var(${level(score?.accuracy)})` }}
                  >
                    {score?.accuracy ?? 0}
                  </ProgressCircle>
                  <span style={{ fontSize: 12 }}>Accuracy</span>
                </Grid.Item>
                <Grid.Item span={6} style={{ textAlign: 'center' }}>
                  <ProgressCircle
                    percent={score?.fluency ?? 0}
                    style={{ '--size': '48px', '--fill-color': `var(${level(score?.fluency)})` }}
                  >
                    {score?.fluency ?? 0}
                  </ProgressCircle>
                  <span style={{ fontSize: 12 }}>Fluency</span>
                </Grid.Item>
                <Grid.Item span={6} style={{ textAlign: 'center' }}>
                  <ProgressCircle
                    percent={score?.completeness ?? 0}
                    style={{ '--size': '48px', '--fill-color': `var(${level(score?.completeness)})` }}
                  >
                    {score?.completeness ?? 0}
                  </ProgressCircle>
                  <span style={{ fontSize: 12 }}>Completeness</span>
                </Grid.Item>
              </Grid>
            </Card>
          )}
        </Space>
      </div>
      <FloatingPanel anchors={[72, 72, 72]} handleDraggingOfContent={false} style={{ '--header-height': 0 }}>
        <Space justify='center' block style={{ margin: '4px 16px', padding: '4px', border: '1px solid #e1e1e1', borderRadius: 16 }}>
          {((selectedItem.conversation[ind].role === 'a') && (ind === 0)) ? (
            <Button onClick={() => handleNext()} disabled={loading || (ind === (selectedItem.conversation.length - 1))} style={{ backgroundColor: '#2e2e2e', borderRadius: 32 }}>
              <Image src={nextIcon} height={32} width={28} fit='fill' />
            </Button>
          ) : (
            <Button onClick={() => handleRecord(!isRec, (selectedItem.conversation[ind].role === 'b'))} disabled={loading || ((selectedItem.conversation[ind].role === 'a') && (ind === (selectedItem.conversation.length - 1))) || ((selectedItem.conversation[ind].role === 'b') && !!result)} style={{ backgroundColor: '#2e2e2e', borderRadius: 32 }}>
              <Image src={(isRec) ? stopIcon : microphoneIcon} height={32} width={32} fit='fill' />
            </Button>
          )}
        </Space>
      </FloatingPanel>
    </div>
  )
}

export default Form
